import { LayoutDashboard, Sparkles } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Steps, Callout, pathFor,
  Diagram, MockKpi, MockTable, MockRow,
} from "../shared";

export default function PagesSummarySection() {
  return (
    <div data-testid="guide-pages-summary">
      <PageHeading
        icon={LayoutDashboard}
        title="Summary (Home)"
        description="The home page. It gives you a one-glance picture of everything that's on the go — a few headline numbers along the top, then all the active items grouped by who has the authority to deal with them."
      />

      <Callout variant="info" title="In plain words">
        Think of the Summary as the <span className="text-white">front dashboard of a car</span>. The cards at the top are your gauges (how many items, how many are late). Below them is the list of active work, sorted into tidy groups so nothing gets lost.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2">
          <li>First thing in the morning, to see the overall state of play.</li>
          <li>To spot how many items are overdue at a glance.</li>
          <li>To find an item quickly and click into it for the full picture.</li>
        </ul>
      </Section>

      <Section title="A picture of the page">
        <Diagram
          frameLabel="Summary (Home)"
          legend={[
            <>The <span className="text-white">headline cards</span> show totals — active items, overdue count, and items by status.</>,
            <>The <span className="text-white">Upward Dispatch Performance</span> card (newer addition) shows the average time, in working days, to send approved items upward, plus how many are overdue. Click it to jump to <Link href={pathFor("pages/md-meetings")} className="text-cyan-300 hover:underline">MD Meetings → Awaiting Dispatch</Link>.</>,
            <>Each <span className="text-white">authority lane</span> is a group you can open or close on its own.</>,
            <>Every row shows the item's title, owner, who it's <span className="text-orange-300">currently with</span> (in orange), its status, due date, and priority. Overdue rows are shown in red. Click any row to open it.</>,
          ]}
        >
          <MockRow>
            <MockKpi label="Active items" value="42" marker={1} />
            <MockKpi label="Overdue" value="7" tone="bad" />
            <MockKpi label="Upward dispatch lag" value="2.4d" marker={2} tone="warn" />
          </MockRow>
          <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
            <p className="text-xs text-white/70 mb-2 flex items-center gap-2">
              <span className="text-white">▸ SDF MD Lane</span>
              <span className="text-2xs text-white/40">(click to collapse)</span>
            </p>
            <MockTable
              columns={["Item", "Owner", "Currently with", "Status", "Due"]}
              rows={[
                ["Q3 portfolio pack", "Sara K.", <span className="text-orange-300">Amal R.</span>, "In Progress", <span className="text-red-300">12 May</span>],
                ["Board resolution 14", "Ravi P.", <span className="text-orange-300">Ravi P.</span>, "Registered", "30 May"],
              ]}
            />
          </div>
        </Diagram>
        <Steps
          steps={[
            <>Read the <span className="text-white">headline cards</span> for the big picture.</>,
            <>Check the <span className="text-white">Upward Dispatch Performance</span> card if you handle sending items upward — click it to go straight there.</>,
            <>Open the <span className="text-white">lane group</span> you care about (each opens and closes independently).</>,
            <>Click any <span className="text-white">row</span> to open that item's full detail page. Items sort by priority then due date; overdue ones are highlighted red.</>,
          ]}
        />
      </Section>

      <Callout variant="info" title="What members see vs admins">
        Members see a <span className="text-white">filtered Summary</span> — only the items they own, currently hold, or have a sub-task on. Admins see <span className="text-white">everything</span>.
      </Callout>

      <Callout variant="tip">
        <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3 text-amber-300" /></span> If a card looks worrying (lots of overdue, or a high dispatch lag), click it or open the matching lane to drill straight into the items behind the number.
      </Callout>
    </div>
  );
}
